import { iconArrowDown, iconArrowUp } from "@kensho/icons";
import { Icon, Table, TableColumn } from "@kensho/neo";

import { Skeleton } from "../../../components/skeleton";
import { HistoricalTranscriptSearchParams } from "../../../types/schemas";
import { TranscriptMetadata } from "../../../types/types";
import { JobStatus } from "./job-status";
import { TranscriptDropdownMenu } from "./transcript-dropdown-menu";
import { TranscriptName } from "./transcript-name/transcript-name";
import { TranscriptNameContextProvider } from "./transcript-name/transcript-name-context";
import { OptionsAction } from "./types";
import { prettyModified } from "./util/pretty-modified";
import { prettyRoundedDuration } from "./util/pretty-rounded-duration";

const TABLE_WIDTH = 768;
const widths = [22, 22, 20, 14, 14, 8];
const total = widths.reduce((a, b) => a + b, 0);
if (total !== 100) {
  throw new Error(`sum of width % should be 100, got ${total}`);
}
let paddingRemoved = 0;
const pixelWidths = widths.map((width, index) => {
  const initialWidth = (width / 100) * TABLE_WIDTH;
  // if first or last subtract 12px for padding otherwise subtract 24px
  const padding = index === 0 || index === widths.length - 1 ? 12 : 24;
  paddingRemoved += padding;
  return Math.floor(initialWidth - padding);
});

// sum pixelWidths to get the total width
const totalWidth = pixelWidths.reduce((a, b) => a + b, 0) + paddingRemoved;
if (totalWidth > TABLE_WIDTH) {
  throw new Error(
    `sum of pixel widths should be less than or equal to ${TABLE_WIDTH}, got ${totalWidth}`,
  );
}

function addVariance(value: number): number {
  return Math.floor(value + value * (Math.random() * 0.3 - 0.15));
}
const varianceGrid = new Array(7)
  .fill(0)
  .map(() => new Array(10).fill(0).map(() => addVariance(85)));

const fakeTranscripts = new Array(10).fill(0).map((_, index) => ({
  id: index,
}));
interface TranscriptMetadataTableProps {
  transcripts: TranscriptMetadata[] | null;
  sort: HistoricalTranscriptSearchParams["sort"];
  direction: HistoricalTranscriptSearchParams["direction"];
  optionsDispatch: React.Dispatch<OptionsAction>;
  className?: string;
  isPending: boolean;
  deleteTranscript: (transcript: TranscriptMetadata) => Promise<void>;
  refreshData: () => void;
}

export function TranscriptMetadataTable({
  transcripts,
  sort,
  direction,
  isPending,
  deleteTranscript,
  optionsDispatch,
  refreshData,
}: TranscriptMetadataTableProps) {
  function getSortingIcon(key: string) {
    if (sort === key) {
      const iconSpec = direction === "ascending" ? iconArrowUp : iconArrowDown;
      return <Icon icon={iconSpec} />;
    }
    return null;
  }

  const sortableColumns: (TableColumn<TranscriptMetadata> & {
    id: HistoricalTranscriptSearchParams["sort"];
  })[] = [
    {
      id: "name",
      label: "Name",
      render: (transcript) => (
        <div className="pl-1" key={transcript.id}>
          <TranscriptName
            refreshData={refreshData}
            id={transcript.id}
            name={transcript.name}
          />
        </div>
      ),
    },
    {
      id: "updated_on",
      label: "Modified",
      render: (transcript) => prettyModified(transcript.updatedOn),
    },
    {
      id: "status",
      label: "Status",
      render: (transcript) => <JobStatus status={transcript.status} />,
    },
    {
      id: "media_duration",
      label: "Length",
      render: (transcript) => transcript.media.duration,
    },
  ];

  const withSortingLabels: TableColumn<TranscriptMetadata>[] =
    sortableColumns.map((column) => ({
      ...column,
      label: (
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            if (sort !== column.id) {
              optionsDispatch({
                type: "sort",
                payload: { sort: column.id, direction: "ascending" },
              });
            } else {
              optionsDispatch({
                type: "sort",
                payload: {
                  sort: column.id,
                  direction:
                    direction === "ascending" ? "descending" : "ascending",
                },
              });
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              if (sort !== column.id) {
                optionsDispatch({
                  type: "sort",
                  payload: { sort: column.id, direction: "ascending" },
                });
              } else {
                optionsDispatch({
                  type: "sort",
                  payload: {
                    sort: column.id,
                    direction:
                      direction === "ascending" ? "descending" : "ascending",
                  },
                });
              }
            }
          }}
        >
          <div className="flex items-center">
            {column.label}
            {getSortingIcon(column.id)}
          </div>
        </div>
      ),
    }));

  const unsortableColumns: TableColumn<TranscriptMetadata>[] = [
    {
      id: "expiration",
      label: "Expiration",
      render: (transcript) => (
        <span>
          {transcript.expires && prettyRoundedDuration(transcript.expires)}
        </span>
      ),
    },
    {
      id: "actions",
      label: null,
      render: (transcript) => (
        <TranscriptDropdownMenu
          transcript={transcript}
          deleteTranscript={deleteTranscript}
        />
      ),
    },
  ];

  const columns: TableColumn<TranscriptMetadata>[] = [
    ...withSortingLabels,
    ...unsortableColumns,
  ].map((column, index) => ({
    ...column,
    label: (
      <div style={{ width: ` ${pixelWidths[index]}px` }}>{column.label}</div>
    ),
  }));

  const loadingColumns: TableColumn<{ id: number }>[] = columns.map(
    (column, index) => ({
      ...column,
      render: (item) => {
        const width = varianceGrid[index][item.id];
        return <Skeleton width={`${width}%`} delayMs={300 * item.id} />;
      },
      sortableBy: undefined,
    }),
  );

  const loading = isPending || !transcripts;

  return (
    <div className="overflow-auto" style={{ width: `${TABLE_WIDTH}px` }}>
      {loading ? (
        <Table
          columns={loadingColumns}
          items={fakeTranscripts}
          getItemKey={(item) => item.id.toString()}
        />
      ) : (
        <TranscriptNameContextProvider>
          <Table
            columns={columns}
            items={transcripts}
            getItemKey={(transcript) => transcript.id}
            getItemProps={(transcript) => ({
              href: `/transcriptions/${transcript.id}`,
            })}
          />
        </TranscriptNameContextProvider>
      )}
    </div>
  );
}
